import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Crown, Star, Gift, TrendingUp, Zap, Shield, Award, ChevronRight, Sparkles, Calendar } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import { Progress } from '../components/ui/progress';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const VIPPage = () => {
    const { user } = useAuth();
    const [vipData, setVipData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchVIPData = async () => {
            try {
                const { data } = await axios.get(`${API}/user/vip`, { withCredentials: true });
                setVipData(data);
            } catch (error) {
                console.error('Error fetching VIP data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchVIPData();
    }, []);

    const getTierIcon = (tier) => {
        switch (tier) {
            case 0: return '🥉';
            case 1: return '🥈';
            case 2: return '🥇';
            case 3: return '💎';
            case 4: return '👑';
            default: return '⭐';
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#050505]">
                <Header />
                <div className="flex justify-center items-center h-[60vh]">
                    <div className="spinner" />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#050505]">
            <Header />
            
            <div className="max-w-5xl mx-auto px-4 py-8">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-8"
                >
                    <div className="flex items-center justify-center gap-2 mb-2">
                        <Crown className="w-8 h-8 text-[#FFD700]" />
                        <h1 className="text-3xl sm:text-4xl font-black text-white">
                            VIP <span className="text-[#FFD700]">Program</span>
                        </h1>
                    </div>
                    <p className="text-[#A0AAB2]">Unlock exclusive rewards as you play</p>
                </motion.div>

                {/* Current VIP Status */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="card-dark p-6 mb-8"
                    style={{ borderColor: vipData?.tier_color + '50' }}
                >
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-4">
                            <div 
                                className="w-20 h-20 rounded-full flex items-center justify-center text-4xl"
                                style={{ backgroundColor: vipData?.tier_color + '20', border: `3px solid ${vipData?.tier_color}` }}
                            >
                                {getTierIcon(vipData?.current_tier)}
                            </div>
                            <div>
                                <p className="text-[#A0AAB2] text-sm">Current Tier</p>
                                <h2 className="text-2xl font-black" style={{ color: vipData?.tier_color }}>
                                    {vipData?.tier_name}
                                </h2>
                                <p className="text-[#A0AAB2] text-sm">
                                    Total Wagered: <span className="text-white font-mono">${vipData?.total_wagered?.toFixed(2)}</span>
                                </p>
                            </div>
                        </div>

                        {/* Current Benefits */}
                        <div className="grid grid-cols-3 gap-4 text-center">
                            <div className="p-3 rounded-lg bg-white/5">
                                <Gift className="w-5 h-5 mx-auto mb-1 text-[#39FF14]" />
                                <p className="text-xs text-[#A0AAB2]">Free Play Bonus</p>
                                <p className="text-lg font-bold text-white">{vipData?.benefits?.daily_bonus}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-white/5">
                                <TrendingUp className="w-5 h-5 mx-auto mb-1 text-[#00FFFF]" />
                                <p className="text-xs text-[#A0AAB2]">Cashback</p>
                                <p className="text-lg font-bold text-white">{vipData?.benefits?.cashback_percent}%</p>
                            </div>
                            <div className="p-3 rounded-lg bg-white/5">
                                <Zap className="w-5 h-5 mx-auto mb-1 text-[#FF007F]" />
                                <p className="text-xs text-[#A0AAB2]">Free Spins</p>
                                <p className="text-lg font-bold text-white">{vipData?.benefits?.free_spins_multiplier}x</p>
                            </div>
                        </div>
                    </div>

                    {/* Progress to Next Tier */}
                    {vipData?.next_tier?.next_tier !== null && (
                        <div className="mt-6 pt-6 border-t border-white/10">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[#A0AAB2] text-sm">Progress to {vipData?.next_tier?.next_tier_name}</span>
                                <span className="text-white font-mono text-sm">
                                    ${vipData?.next_tier?.remaining?.toFixed(2)} remaining
                                </span>
                            </div>
                            <Progress value={vipData?.next_tier?.progress || 0} className="h-3" />
                        </div>
                    )}

                    {/* Login Streak */}
                    <div className="mt-6 pt-6 border-t border-white/10">
                        <div className="flex items-center gap-3">
                            <Calendar className="w-5 h-5 text-[#FFD700]" />
                            <span className="text-[#A0AAB2]">Login Streak:</span>
                            <div className="flex gap-1">
                                {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                                    <div
                                        key={day}
                                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                                            day <= (vipData?.login_streak || 0)
                                                ? 'bg-[#FFD700] text-black'
                                                : 'bg-white/10 text-[#A0AAB2]'
                                        }`}
                                    >
                                        {day}
                                    </div>
                                ))}
                            </div>
                            <span className="text-[#FFD700] font-bold ml-2">
                                +{((vipData?.login_streak || 1) - 1) * 5} bonus
                            </span>
                        </div>
                    </div>
                </motion.div>

                {/* All VIP Tiers */}
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Award className="w-5 h-5 text-[#FFD700]" />
                    VIP Tiers
                </h3>
                <div className="space-y-4">
                    {vipData?.all_tiers?.map((tier, index) => (
                        <motion.div
                            key={tier.tier}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className={`card-dark p-4 transition-all ${
                                tier.tier === vipData?.current_tier 
                                    ? 'border-2' 
                                    : tier.unlocked 
                                        ? 'opacity-80' 
                                        : 'opacity-50'
                            }`}
                            style={{ 
                                borderColor: tier.tier === vipData?.current_tier ? tier.color : 'transparent',
                                background: tier.tier === vipData?.current_tier ? `linear-gradient(90deg, ${tier.color}10 0%, transparent 100%)` : undefined
                            }}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div 
                                        className="w-14 h-14 rounded-full flex items-center justify-center text-2xl"
                                        style={{ backgroundColor: tier.color + '20' }}
                                    >
                                        {getTierIcon(tier.tier)}
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h4 className="text-lg font-bold" style={{ color: tier.color }}>
                                                {tier.name}
                                            </h4>
                                            {tier.tier === vipData?.current_tier && (
                                                <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-[#FFD700] text-black">
                                                    CURRENT
                                                </span>
                                            )}
                                            {tier.unlocked && tier.tier !== vipData?.current_tier && (
                                                <Shield className="w-4 h-4 text-[#39FF14]" />
                                            )}
                                        </div>
                                        <p className="text-[#A0AAB2] text-sm">
                                            Wager ${tier.min_wagered.toLocaleString()} to unlock
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="flex items-center gap-6 text-sm">
                                    <div className="text-center">
                                        <p className="text-[#A0AAB2]">Daily</p>
                                        <p className="font-bold text-white">{tier.daily_bonus}</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-[#A0AAB2]">Cashback</p>
                                        <p className="font-bold text-white">{tier.cashback}%</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-[#A0AAB2]">Free Spins</p>
                                        <p className="font-bold text-white">{tier.free_spins_multiplier}x</p>
                                    </div>
                                    {!tier.unlocked && (
                                        <ChevronRight className="w-5 h-5 text-[#A0AAB2]" />
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Benefits Explanation */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4"
                >
                    <div className="card-dark p-5 text-center">
                        <Gift className="w-8 h-8 mx-auto mb-3 text-[#39FF14]" />
                        <h4 className="font-bold text-white mb-2">Free Play Bonus</h4>
                        <p className="text-[#A0AAB2] text-sm">
                            Load $20+ in a single deposit to unlock your daily free play credits. Play any game — winnings are real cash.
                        </p>
                    </div>
                    <div className="card-dark p-5 text-center">
                        <TrendingUp className="w-8 h-8 mx-auto mb-3 text-[#00FFFF]" />
                        <h4 className="font-bold text-white mb-2">Cashback</h4>
                        <p className="text-[#A0AAB2] text-sm">
                            Get a percentage of your losses back weekly. Higher VIP tiers = higher cashback rates!
                        </p>
                    </div>
                    <div className="card-dark p-5 text-center">
                        <Zap className="w-8 h-8 mx-auto mb-3 text-[#FF007F]" />
                        <h4 className="font-bold text-white mb-2">Free Spins Multiplier</h4>
                        <p className="text-[#A0AAB2] text-sm">
                            When you earn free spins, your VIP tier multiplies the reward!
                        </p>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default VIPPage;
